import {getListingThumbnail} from "@hosttools/core/shared/utils/utils.listing";
import {type FC, memo, useMemo} from "react";

import {useMobileView} from "../../hooks";
import type {Listing} from "../../models/listing";
import ListingChannelsLogo from "../ListingChannelsLogo";
import ListingThumbnail from "../ListingThumbnail";
import {Badge, BadgeText} from "../ui/badge";
import {HStack} from "../ui/hstack";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

type Size = "xs" | "sm" | "md";

type Props = {
    listing: Listing;
    size?: Size;
    color?: "blueGray.600" | "blueGray.800";
    numberOfLines?: 1 | 2;
    includeChannel?: boolean;
    includeAddress?: boolean;
    primaryText?: string;
    className?: string;
    listingStatus?: string;
    thumbnailOnMobile?: boolean;
};

const ListingSlat: FC<Props> = ({
    listing,
    size = "md",
    color,
    numberOfLines = 2,
    includeChannel = true,
    includeAddress = false,
    primaryText,
    className,
    listingStatus,
    thumbnailOnMobile = true
}) => {
    const variants = useMemo(() => {
        return {
            xs: {
                textSize: "xs",
                space: "xs",
                channelLogoSize: "xs"
            } as const,
            sm: {
                textSize: "sm",
                space: "sm",
                channelLogoSize: "sm"
            } as const,
            md: {
                textSize: "md" as const,
                space: "md",
                channelLogoSize: "sm"
            } as const
        } satisfies Record<Size, unknown>;
    }, []);

    const styles = variants[size];
    const isMobile = useMobileView();
    const thumbnail = useMemo(() => getListingThumbnail(listing), [listing]);

    return (
        <HStack
            className={`items-center w-full${className ? ` ${className}` : ""}`}
            space={styles.space}
            testID="listing-slat"
        >
            {isMobile ? (
                thumbnailOnMobile ? (
                    <ListingThumbnail size={size} thumbnail={thumbnail} />
                ) : null
            ) : (
                <ListingThumbnail size={size} thumbnail={thumbnail} />
            )}
            <VStack className="flex-1" space="xs">
                <Text
                    color={color}
                    numberOfLines={numberOfLines}
                    testID="listing-title"
                    variant={styles.textSize}
                >
                    {listing.name}
                </Text>
                {includeChannel && (
                    <ListingChannelsLogo listing={listing} size={styles.channelLogoSize} />
                )}
                {primaryText && (
                    <Text color="blueGray.400" numberOfLines={1} variant={styles.textSize}>
                        {primaryText}
                    </Text>
                )}
                {includeAddress && (
                    <Text color="blueGray.600" numberOfLines={2} variant={styles.textSize}>
                        {listing.airbnbAddress}
                    </Text>
                )}
            </VStack>
            {listingStatus && (
                <Badge
                    action={listingStatus === "listed" ? "info" : "default"}
                    className="ml-auto"
                    variant="subtle"
                >
                    <BadgeText isTruncated>{listingStatus}</BadgeText>
                </Badge>
            )}
        </HStack>
    );
};

export default memo(ListingSlat);
